import React from "react";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Table,
  Input,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { UsuarioDto } from "../../../types/ajustes";
import SectionState from "./SectionState";

interface UsuariosTabProps {
  usuarios: UsuarioDto[];
  loading: boolean;
  error: string | null;
  onReload: () => void;
  onCreate: () => void;
  onEdit: (usuario: UsuarioDto) => void;
  onDelete: (usuario: UsuarioDto) => void;
  showDeleted: boolean;
  onToggleShowDeleted: (value: boolean) => void;
  onReactivate: (usuario: UsuarioDto) => void;
  search: string;
  onSearchChange: (value: string) => void;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canReactivate: boolean;
}

const UsuariosTab = ({
  usuarios,
  loading,
  error,
  onReload,
  onCreate,
  onEdit,
  onDelete,
  showDeleted,
  onToggleShowDeleted,
  onReactivate,
  search,
  onSearchChange,
  canCreate,
  canEdit,
  canDelete,
  canReactivate,
}: UsuariosTabProps) => {
  const filtered = usuarios.filter((usuario) => {
    const isEliminado = Boolean(usuario.eliminado || usuario.eliminadoEn);
    if (!showDeleted && isEliminado) {
      return false;
    }
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      usuario.nombreUsuario.toLowerCase().includes(term) ||
      (usuario.correoCorporativo ?? "").toLowerCase().includes(term) ||
      (usuario.numeroCi ?? "").toLowerCase().includes(term) ||
      (usuario.empresas ?? []).some(
        (empresa) =>
          (empresa.nombre ?? "").toLowerCase().includes(term) ||
          (empresa.codigo ?? "").toLowerCase().includes(term)
      )
    );
  });

  return (
    <Card>
      <CardBody>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <div>
            <CardTitle tag="h5" className="mb-1">
              Usuarios
            </CardTitle>
            <p className="text-muted mb-0">Gestiona las cuentas y roles asignados.</p>
          </div>
          <div className="d-flex gap-2">
            <Button color="light" onClick={onReload}>
              Actualizar
            </Button>
            <Button color="primary" onClick={onCreate} disabled={!canCreate}>
              Nuevo usuario
            </Button>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-3 mb-3">
          <Input
            type="search"
            placeholder="Buscar usuario o email..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-auto flex-grow-1"
          />
          <div className="form-check form-switch">
            <Input
              id="usuarios-deleted"
              type="checkbox"
              role="switch"
              checked={showDeleted}
              onChange={(e) => onToggleShowDeleted(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="usuarios-deleted">
              Mostrar inactivos
            </label>
          </div>
        </div>

        <SectionState
          loading={loading}
          error={error}
          description="Cargando usuarios..."
          onRetry={onReload}
        />

        {!loading && !error && (
          <div className="table-responsive">
            <Table className="align-middle">
              <thead className="table-light">
                <tr>
                  <th>Usuario</th>
                  <th>N.º CI</th>
                  <th>Correo</th>
                  <th>Empresas</th>
                  <th>Roles</th>
                  <th>Estado</th>
                  <th className="text-center">Acción</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No se encontraron usuarios
                    </td>
                  </tr>
                )}
                {filtered.map((usuario) => {
                  const isEliminado = Boolean(usuario.eliminado || usuario.eliminadoEn);
                  return (
                  <tr key={usuario.id}>
                    <td className="fw-medium">{usuario.nombreUsuario}</td>
                    <td>{usuario.numeroCi || "Sin dato"}</td>
                    <td>{usuario.correoCorporativo || "Sin correo"}</td>
                    <td>
                      {usuario.empresas && usuario.empresas.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1">
                          {usuario.empresas.map((empresa) => (
                            <span
                              key={`${usuario.id}-empresa-${empresa.id}`}
                              className="badge bg-light text-body border"
                            >
                              {empresa.nombre ?? empresa.codigo ?? `ID ${empresa.id}`}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">Sin asignar</span>
                      )}
                    </td>
                    <td>
                      {usuario.roles.length === 0
                        ? "Sin rol"
                        : usuario.roles.map((rol) => rol.nombre).join(", ")}
                    </td>
                    <td>
                        {isEliminado ? (
                        <span className="badge bg-danger-subtle text-danger">Inactivo</span>
                      ) : (
                        <span className="badge bg-success-subtle text-success">Activo</span>
                      )}
                    </td>
                    <td className="text-center">
                      <UncontrolledDropdown>
                        <DropdownToggle tag="button" className="btn btn-soft-secondary btn-sm">
                          <i className="ri-more-fill align-middle" />
                        </DropdownToggle>
                        <DropdownMenu className="dropdown-menu-end">
                          <DropdownItem
                            disabled={!canEdit}
                            onClick={() => {
                              if (!canEdit) return;
                              onEdit(usuario);
                            }}
                          >
                            <i className="ri-pencil-fill align-bottom me-2 text-muted" />
                            Editar
                          </DropdownItem>
                          {isEliminado ? (
                            <DropdownItem
                              disabled={!canReactivate}
                              onClick={() => {
                                if (!canReactivate) return;
                                const confirmacion = window.confirm(
                                  "¿Deseas reactivar este usuario?"
                                );
                                if (confirmacion) {
                                  onReactivate(usuario);
                                }
                              }}
                            >
                              <i className="ri-user-follow-line align-bottom me-2 text-muted" />
                              Activar
                            </DropdownItem>
                          ) : (
                            <DropdownItem
                              disabled={!canDelete}
                              onClick={() => {
                                if (!canDelete) return;
                                const confirmacion = window.confirm(
                                  "¿Deseas inactivar este usuario?"
                                );
                                if (confirmacion) {
                                  onDelete(usuario);
                                }
                              }}
                            >
                              <i className="ri-user-unfollow-line align-bottom me-2 text-muted" />
                              Inactivar
                            </DropdownItem>
                          )}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </td>
                  </tr>
                )})}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default UsuariosTab;
